import { Option, Schema } from "effect";
import {
  ProjectIdSchema,
  RequestIdSchema,
  ThreadIdSchema,
  UserIdSchema,
  WorkspaceIdSchema,
} from "./ids";
import {
  CanonicalIdentifierSchema,
  IsoDateTimeSchema,
  NonNegativeSafeIntegerSchema,
  PapersFlowBoundaryParseOptions,
  toPapersFlowValidationSchema,
} from "./standard";
import { utf8ByteLength } from "./utf8";

export const AGENT_BROWSER_MAX_TABS = 8;
export const AGENT_BROWSER_MAX_ALLOWED_DOMAINS = 8;
export const AGENT_BROWSER_MAX_ELEMENTS = 80;
export const AGENT_BROWSER_MAX_URL_BYTES = 2_048;
export const AGENT_BROWSER_MAX_PAGE_CONTENT_BYTES = 16_000;
export const AGENT_BROWSER_MAX_SCREENSHOT_BYTES = 2 * 1_024 * 1_024;
export const AGENT_BROWSER_MAX_SCREENSHOT_BASE64_BYTES = 2_796_204;
export const AGENT_BROWSER_HANDLE_TTL_MS = 10 * 60 * 1_000;
export const AGENT_BROWSER_START_PATH = "/internal/v1/agent-browser/start";
export const AGENT_BROWSER_COMMAND_PATH = "/internal/v1/agent-browser/command";
export const AGENT_BROWSER_CLOSE_PATH = "/internal/v1/agent-browser/close";

const PositiveSafeIntegerSchema = Schema.Int.check(
  Schema.isGreaterThanOrEqualTo(1),
  Schema.isLessThanOrEqualTo(Number.MAX_SAFE_INTEGER),
);
const BoundedIdentifierSchema = CanonicalIdentifierSchema.check(Schema.isMaxLength(180));
const BrowserHandleSchema = Schema.String.check(
  Schema.isMinLength(80),
  Schema.isMaxLength(8_192),
  Schema.makeFilter(
    (value) => /^[A-Za-z0-9_-]+\.[a-f0-9]{64}$/u.test(value) || "must be a signed browser handle",
  ),
);
const OptionalBoundedBrowserTextSchema = Schema.String.check(
  Schema.makeFilter(
    (value) =>
      (value === value.trim() && !/\p{Cc}/u.test(value) && utf8ByteLength(value) <= 512) ||
      "must be canonical text of at most 512 UTF-8 bytes",
  ),
);
const BoundedBrowserPageContentSchema = Schema.String.check(
  Schema.makeFilter(
    (value) =>
      (value === value.trim() &&
        !/(?![\n\t])\p{Cc}/u.test(value) &&
        utf8ByteLength(value) <= AGENT_BROWSER_MAX_PAGE_CONTENT_BYTES) ||
      `must be canonical page content of at most ${String(AGENT_BROWSER_MAX_PAGE_CONTENT_BYTES)} UTF-8 bytes`,
  ),
);

const ReservedHostnameSuffixes = [".internal", ".invalid", ".local", ".localhost", ".test"];
const decodeUrl = Schema.decodeUnknownOption(Schema.URLFromString);

function isIpv4Address(hostname: string): boolean {
  const segments = hostname.split(".");
  return (
    segments.length === 4 &&
    segments.every((segment) => {
      if (!/^\d{1,3}$/u.test(segment)) return false;
      const value = Number(segment);
      return value >= 0 && value <= 255 && String(value) === segment;
    })
  );
}

export function agentBrowserPublicHostnameIssue(hostname: string): true | string {
  if (
    hostname.length < 3 ||
    hostname.length > 253 ||
    hostname !== hostname.toLowerCase() ||
    hostname.endsWith(".") ||
    hostname.includes(":") ||
    isIpv4Address(hostname) ||
    hostname === "localhost" ||
    ReservedHostnameSuffixes.some((suffix) => hostname.endsWith(suffix))
  ) {
    return "must be a canonical public DNS hostname";
  }
  const labels = hostname.split(".");
  if (
    labels.length < 2 ||
    labels.some(
      (label) =>
        label.length < 1 || label.length > 63 || !/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/u.test(label),
    )
  ) {
    return "must be a canonical public DNS hostname";
  }
  return true;
}

export function agentBrowserPublicUrlIssue(value: string): true | string {
  if (
    value !== value.trim() ||
    /\p{Cc}|\s/u.test(value) ||
    utf8ByteLength(value) > AGENT_BROWSER_MAX_URL_BYTES
  ) {
    return "must be a bounded public HTTPS URL";
  }
  const parsed = decodeUrl(value);
  if (Option.isNone(parsed)) return "must be a bounded public HTTPS URL";
  const url = parsed.value;
  if (
    url.protocol !== "https:" ||
    url.username !== "" ||
    url.password !== "" ||
    agentBrowserPublicHostnameIssue(url.hostname) !== true
  ) {
    return "must be a credential-free public HTTPS URL with a DNS hostname";
  }
  return true;
}

export function agentBrowserHostnameForUrl(value: string): string {
  const parsed = decodeUrl(value);
  if (agentBrowserPublicUrlIssue(value) !== true || Option.isNone(parsed)) {
    throw new Error("Agent Browser URL is outside the public HTTPS policy");
  }
  return parsed.value.hostname;
}

export const AgentBrowserPublicHostnameSchema = Schema.String.check(
  Schema.makeFilter(agentBrowserPublicHostnameIssue),
);
export const AgentBrowserPublicUrlSchema = Schema.String.check(
  Schema.makeFilter(agentBrowserPublicUrlIssue),
);
export const AgentBrowserAllowedDomainsSchema = Schema.Array(
  AgentBrowserPublicHostnameSchema,
).check(
  Schema.isMinLength(1),
  Schema.isMaxLength(AGENT_BROWSER_MAX_ALLOWED_DOMAINS),
  Schema.makeFilter(
    (domains) => new Set(domains).size === domains.length || "allowed domains must be unique",
  ),
);

export const AgentBrowserRunScopeSchema = Schema.Struct({
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  threadId: ThreadIdSchema,
  runId: BoundedIdentifierSchema,
  attemptId: BoundedIdentifierSchema,
  runGeneration: PositiveSafeIntegerSchema,
  attemptGeneration: PositiveSafeIntegerSchema,
  actorUserId: UserIdSchema,
});

export const AgentBrowserStartRequestSchema = Schema.Struct({
  requestId: RequestIdSchema,
  scope: AgentBrowserRunScopeSchema,
  url: AgentBrowserPublicUrlSchema,
  allowedDomains: AgentBrowserAllowedDomainsSchema,
}).check(
  Schema.makeFilter(
    (request) =>
      request.allowedDomains.includes(agentBrowserHostnameForUrl(request.url)) ||
      "the initial URL hostname must be present in allowedDomains",
  ),
);

export const AgentBrowserTargetIdSchema = BoundedIdentifierSchema.pipe(
  Schema.brand("AgentBrowserTargetId"),
);
export const AgentBrowserDocumentIdSchema = BoundedIdentifierSchema.pipe(
  Schema.brand("AgentBrowserDocumentId"),
);
export const AgentBrowserSnapshotIdSchema = BoundedIdentifierSchema.pipe(
  Schema.brand("AgentBrowserSnapshotId"),
);
export const AgentBrowserTabIdSchema = BoundedIdentifierSchema.pipe(
  Schema.brand("AgentBrowserTabId"),
);
export const AgentBrowserElementRefSchema = Schema.String.check(
  Schema.makeFilter(
    (value) => /^e[1-9][0-9]{0,2}$/u.test(value) || "must be a browser element ref",
  ),
).pipe(Schema.brand("AgentBrowserElementRef"));

export const AgentBrowserViewportSchema = Schema.Struct({
  width: Schema.Int.check(Schema.isBetween({ minimum: 320, maximum: 1_600 })),
  height: Schema.Int.check(Schema.isBetween({ minimum: 240, maximum: 1_200 })),
});
export const AgentBrowserElementRectSchema = Schema.Struct({
  x: Schema.Finite,
  y: Schema.Finite,
  width: Schema.Finite.check(Schema.isGreaterThanOrEqualTo(0)),
  height: Schema.Finite.check(Schema.isGreaterThanOrEqualTo(0)),
});
export const AgentBrowserElementRoleSchema = Schema.Literals([
  "link",
  "button",
  "textbox",
  "checkbox",
  "radio",
  "combobox",
  "other",
]);
export const AgentBrowserSemanticLocatorSchema = Schema.Struct({
  domIndex: NonNegativeSafeIntegerSchema.check(Schema.isLessThan(512)),
  tag: Schema.String.check(
    Schema.isMinLength(1),
    Schema.isMaxLength(32),
    Schema.makeFilter((value) => /^[a-z][a-z0-9-]*$/u.test(value) || "must be a tag name"),
  ),
  role: AgentBrowserElementRoleSchema,
  name: OptionalBoundedBrowserTextSchema,
  href: Schema.optionalKey(AgentBrowserPublicUrlSchema),
});
export const AgentBrowserObservedElementSchema = Schema.Struct({
  locator: AgentBrowserSemanticLocatorSchema,
  rect: AgentBrowserElementRectSchema,
});
export const AgentBrowserPublicElementSchema = Schema.Struct({
  ref: AgentBrowserElementRefSchema,
  role: AgentBrowserElementRoleSchema,
  name: OptionalBoundedBrowserTextSchema,
  href: Schema.optionalKey(AgentBrowserPublicUrlSchema),
  rect: AgentBrowserElementRectSchema,
});

export const AgentBrowserWorkerTabSchema = Schema.Struct({
  targetId: AgentBrowserTargetIdSchema,
  url: AgentBrowserPublicUrlSchema,
  title: OptionalBoundedBrowserTextSchema,
  documentId: AgentBrowserDocumentIdSchema,
});
export const AgentBrowserTabSchema = Schema.Struct({
  tabId: AgentBrowserTabIdSchema,
  url: AgentBrowserPublicUrlSchema,
  title: OptionalBoundedBrowserTextSchema,
  documentId: AgentBrowserDocumentIdSchema,
});
export const AgentBrowserWorkerSnapshotSchema = Schema.Struct({
  tab: AgentBrowserWorkerTabSchema,
  viewport: AgentBrowserViewportSchema,
  ariaSnapshot: BoundedBrowserPageContentSchema,
  truncated: Schema.Boolean,
  elements: Schema.Array(AgentBrowserObservedElementSchema).check(
    Schema.isMaxLength(AGENT_BROWSER_MAX_ELEMENTS),
  ),
});
export const AgentBrowserSnapshotSchema = Schema.Struct({
  snapshotId: AgentBrowserSnapshotIdSchema,
  tabId: AgentBrowserTabIdSchema,
  url: AgentBrowserPublicUrlSchema,
  title: OptionalBoundedBrowserTextSchema,
  documentId: AgentBrowserDocumentIdSchema,
  viewport: AgentBrowserViewportSchema,
  ariaSnapshot: BoundedBrowserPageContentSchema,
  truncated: Schema.Boolean,
  elements: Schema.Array(AgentBrowserPublicElementSchema).check(
    Schema.isMaxLength(AGENT_BROWSER_MAX_ELEMENTS),
  ),
  untrusted: Schema.Literal(true),
});
export const AgentBrowserWorkerPageTextSchema = Schema.Struct({
  tab: AgentBrowserWorkerTabSchema,
  text: BoundedBrowserPageContentSchema,
  truncated: Schema.Boolean,
});
export const AgentBrowserPageTextSchema = Schema.Struct({
  tabId: AgentBrowserTabIdSchema,
  url: AgentBrowserPublicUrlSchema,
  title: OptionalBoundedBrowserTextSchema,
  documentId: AgentBrowserDocumentIdSchema,
  text: BoundedBrowserPageContentSchema,
  truncated: Schema.Boolean,
  untrusted: Schema.Literal(true),
});

export const AgentBrowserCommandSchema = Schema.Union([
  Schema.Struct({ kind: Schema.Literal("list-tabs") }),
  Schema.Struct({ kind: Schema.Literal("open-tab"), url: AgentBrowserPublicUrlSchema }),
  Schema.Struct({ kind: Schema.Literal("focus-tab"), targetId: AgentBrowserTargetIdSchema }),
  Schema.Struct({ kind: Schema.Literal("close-tab"), targetId: AgentBrowserTargetIdSchema }),
  Schema.Struct({
    kind: Schema.Literal("navigate"),
    targetId: AgentBrowserTargetIdSchema,
    url: AgentBrowserPublicUrlSchema,
  }),
  Schema.Struct({ kind: Schema.Literal("snapshot"), targetId: AgentBrowserTargetIdSchema }),
  Schema.Struct({ kind: Schema.Literal("read-text"), targetId: AgentBrowserTargetIdSchema }),
  Schema.Struct({
    kind: Schema.Literal("screenshot"),
    targetId: AgentBrowserTargetIdSchema,
    annotate: Schema.Boolean,
  }),
  Schema.Struct({
    kind: Schema.Literal("navigate-to-link"),
    targetId: AgentBrowserTargetIdSchema,
    expectedDocumentId: AgentBrowserDocumentIdSchema,
    locator: AgentBrowserSemanticLocatorSchema.check(
      Schema.makeFilter(
        (locator) =>
          (locator.role === "link" && locator.href !== undefined) ||
          "navigate-to-link requires a captured HTTPS link",
      ),
    ),
  }),
  Schema.Struct({
    kind: Schema.Literal("scroll"),
    targetId: AgentBrowserTargetIdSchema,
    expectedDocumentId: AgentBrowserDocumentIdSchema,
    direction: Schema.Literals(["up", "down"]),
    amount: Schema.Int.check(Schema.isBetween({ minimum: 120, maximum: 1_200 })),
  }),
]);
export const AgentBrowserCommandRequestSchema = Schema.Struct({
  requestId: RequestIdSchema,
  handle: BrowserHandleSchema,
  command: AgentBrowserCommandSchema,
});
export const AgentBrowserCloseRequestSchema = Schema.Struct({
  requestId: RequestIdSchema,
  handle: BrowserHandleSchema,
});
export const AgentBrowserStartResultSchema = Schema.Struct({
  handle: BrowserHandleSchema,
  tabs: Schema.Array(AgentBrowserWorkerTabSchema).check(
    Schema.isMinLength(1),
    Schema.isMaxLength(AGENT_BROWSER_MAX_TABS),
  ),
  expiresAt: PositiveSafeIntegerSchema,
});

const AgentBrowserScreenshotBase64Schema = Schema.String.check(
  Schema.isMinLength(4),
  Schema.isMaxLength(AGENT_BROWSER_MAX_SCREENSHOT_BASE64_BYTES),
  Schema.makeFilter(
    (value) =>
      /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/u.test(value) ||
      "must be canonical base64",
  ),
);
function decodedBase64Length(value: string): number {
  const padding = value.endsWith("==") ? 2 : value.endsWith("=") ? 1 : 0;
  return (value.length / 4) * 3 - padding;
}
export const AgentBrowserWorkerScreenshotSchema = Schema.Struct({
  captureId: BoundedIdentifierSchema,
  mediaType: Schema.Literal("image/jpeg"),
  byteSize: PositiveSafeIntegerSchema.check(
    Schema.isLessThanOrEqualTo(AGENT_BROWSER_MAX_SCREENSHOT_BYTES),
  ),
  dataBase64: AgentBrowserScreenshotBase64Schema,
}).check(
  Schema.makeFilter(
    (capture) =>
      decodedBase64Length(capture.dataBase64) === capture.byteSize ||
      "screenshot byteSize must match its base64 payload",
  ),
);

export const AgentBrowserCommandResultSchema = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("tabs"),
    tabs: Schema.Array(AgentBrowserWorkerTabSchema).check(
      Schema.isMaxLength(AGENT_BROWSER_MAX_TABS),
    ),
  }),
  Schema.Struct({ kind: Schema.Literal("tab-opened"), tab: AgentBrowserWorkerTabSchema }),
  Schema.Struct({ kind: Schema.Literal("tab-focused"), tab: AgentBrowserWorkerTabSchema }),
  Schema.Struct({
    kind: Schema.Literal("tab-closed"),
    targetId: AgentBrowserTargetIdSchema,
    tabs: Schema.Array(AgentBrowserWorkerTabSchema).check(
      Schema.isMaxLength(AGENT_BROWSER_MAX_TABS),
    ),
  }),
  Schema.Struct({ kind: Schema.Literal("navigated"), tab: AgentBrowserWorkerTabSchema }),
  Schema.Struct({ kind: Schema.Literal("link-navigated"), tab: AgentBrowserWorkerTabSchema }),
  Schema.Struct({ kind: Schema.Literal("snapshot"), snapshot: AgentBrowserWorkerSnapshotSchema }),
  Schema.Struct({ kind: Schema.Literal("page-text"), pageText: AgentBrowserWorkerPageTextSchema }),
  Schema.Struct({ kind: Schema.Literal("scrolled"), snapshot: AgentBrowserWorkerSnapshotSchema }),
  Schema.Struct({
    kind: Schema.Literal("screenshot"),
    snapshot: AgentBrowserWorkerSnapshotSchema,
    screenshot: AgentBrowserWorkerScreenshotSchema,
  }),
]);
export const AgentBrowserCloseResultSchema = Schema.Struct({ closed: Schema.Literal(true) });

export const AgentBrowserWorkerErrorCodeSchema = Schema.Literals([
  "AGENT_BROWSER_ACCESS_DENIED",
  "AGENT_BROWSER_AUTH_INVALID",
  "AGENT_BROWSER_INVALID_INPUT",
  "AGENT_BROWSER_POLICY_DENIED",
  "AGENT_BROWSER_SESSION_EXPIRED",
  "AGENT_BROWSER_SESSION_NOT_FOUND",
  "AGENT_BROWSER_TAB_LIMIT_REACHED",
  "AGENT_BROWSER_TAB_NOT_FOUND",
  "AGENT_BROWSER_STALE_DOCUMENT",
  "AGENT_BROWSER_RESPONSE_TOO_LARGE",
  "AGENT_BROWSER_RATE_LIMITED",
  "AGENT_BROWSER_UNAVAILABLE",
  "AGENT_BROWSER_FAILED",
]);
export const AgentBrowserWorkerEnvelopeSchema = Schema.Union([
  Schema.Struct({
    ok: Schema.Literal(true),
    data: Schema.Union([
      AgentBrowserStartResultSchema,
      AgentBrowserCommandResultSchema,
      AgentBrowserCloseResultSchema,
    ]),
  }),
  Schema.Struct({
    ok: Schema.Literal(false),
    error: Schema.Struct({
      code: AgentBrowserWorkerErrorCodeSchema,
      message: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(240)),
      requestId: RequestIdSchema,
      retryable: Schema.Boolean,
    }),
  }),
]);

const OpenToolInputSchema = Schema.Struct({
  action: Schema.Literal("open"),
  url: AgentBrowserPublicUrlSchema,
  additionalDomains: Schema.optionalKey(
    Schema.Array(AgentBrowserPublicHostnameSchema).check(
      Schema.isMaxLength(AGENT_BROWSER_MAX_ALLOWED_DOMAINS - 1),
      Schema.makeFilter(
        (domains) =>
          new Set(domains).size === domains.length || "additional domains must be unique",
      ),
    ),
  ),
});
export const AgentBrowserToolInputSchema = Schema.Union([
  OpenToolInputSchema,
  Schema.Struct({ action: Schema.Literal("tabs") }),
  Schema.Struct({ action: Schema.Literal("open_tab"), url: AgentBrowserPublicUrlSchema }),
  Schema.Struct({ action: Schema.Literal("focus_tab"), tabId: AgentBrowserTabIdSchema }),
  Schema.Struct({ action: Schema.Literal("close_tab"), tabId: AgentBrowserTabIdSchema }),
  Schema.Struct({
    action: Schema.Literal("navigate"),
    tabId: AgentBrowserTabIdSchema,
    url: AgentBrowserPublicUrlSchema,
  }),
  Schema.Struct({ action: Schema.Literal("snapshot"), tabId: AgentBrowserTabIdSchema }),
  Schema.Struct({ action: Schema.Literal("text"), tabId: AgentBrowserTabIdSchema }),
  Schema.Struct({
    action: Schema.Literal("screenshot"),
    tabId: AgentBrowserTabIdSchema,
    annotate: Schema.optionalKey(Schema.Boolean),
  }),
  Schema.Struct({
    action: Schema.Literal("navigate_to_link"),
    tabId: AgentBrowserTabIdSchema,
    snapshotId: AgentBrowserSnapshotIdSchema,
    ref: AgentBrowserElementRefSchema,
  }),
  Schema.Struct({
    action: Schema.Literal("scroll"),
    tabId: AgentBrowserTabIdSchema,
    documentId: AgentBrowserDocumentIdSchema,
    direction: Schema.Literals(["up", "down"]),
    amount: Schema.optionalKey(
      Schema.Int.check(Schema.isBetween({ minimum: 120, maximum: 1_200 })),
    ),
  }),
  Schema.Struct({ action: Schema.Literal("close") }),
]);

export const AgentBrowserScreenshotReceiptSchema = Schema.Struct({
  captureId: BoundedIdentifierSchema,
  snapshotId: AgentBrowserSnapshotIdSchema,
  tabId: AgentBrowserTabIdSchema,
  documentId: AgentBrowserDocumentIdSchema,
  mediaType: Schema.Literal("image/jpeg"),
  byteSize: PositiveSafeIntegerSchema.check(
    Schema.isLessThanOrEqualTo(AGENT_BROWSER_MAX_SCREENSHOT_BYTES),
  ),
  annotated: Schema.Boolean,
});
export const AgentBrowserToolOutputSchema = Schema.Struct({
  action: Schema.Literals([
    "open",
    "tabs",
    "open_tab",
    "focus_tab",
    "close_tab",
    "navigate",
    "snapshot",
    "text",
    "screenshot",
    "navigate_to_link",
    "scroll",
    "close",
  ]),
  status: Schema.Literals(["active", "closed", "cleanup_uncertain"]),
  activeTabId: Schema.optionalKey(AgentBrowserTabIdSchema),
  tabs: Schema.Array(AgentBrowserTabSchema).check(Schema.isMaxLength(AGENT_BROWSER_MAX_TABS)),
  snapshot: Schema.optionalKey(AgentBrowserSnapshotSchema),
  pageText: Schema.optionalKey(AgentBrowserPageTextSchema),
  screenshot: Schema.optionalKey(AgentBrowserScreenshotReceiptSchema),
}).check(
  Schema.makeFilter(
    (output) =>
      output.screenshot === undefined ||
      output.snapshot !== undefined ||
      "a screenshot receipt requires its accessibility snapshot",
  ),
  Schema.makeFilter(
    (output) =>
      output.activeTabId === undefined ||
      output.tabs.some((tab) => tab.tabId === output.activeTabId) ||
      "activeTabId must name an open tab",
  ),
  Schema.makeFilter(
    (output) =>
      output.status === "active" ||
      (output.action === "close" &&
        output.activeTabId === undefined &&
        output.tabs.length === 0 &&
        output.snapshot === undefined &&
        output.pageText === undefined &&
        output.screenshot === undefined) ||
      "a terminal browser projection must be an empty close result",
  ),
  Schema.makeFilter(
    (output) =>
      output.snapshot === undefined ||
      output.tabs.some(
        (tab) =>
          tab.tabId === output.snapshot?.tabId &&
          tab.documentId === output.snapshot.documentId &&
          tab.url === output.snapshot.url,
      ) ||
      "a snapshot must identify its matching open tab",
  ),
  Schema.makeFilter(
    (output) =>
      output.pageText === undefined ||
      output.tabs.some(
        (tab) =>
          tab.tabId === output.pageText?.tabId &&
          tab.documentId === output.pageText.documentId &&
          tab.url === output.pageText.url,
      ) ||
      "page text must identify its matching open tab",
  ),
  Schema.makeFilter(
    (output) =>
      output.screenshot === undefined ||
      (output.snapshot !== undefined &&
        output.screenshot.snapshotId === output.snapshot.snapshotId &&
        output.screenshot.tabId === output.snapshot.tabId &&
        output.screenshot.documentId === output.snapshot.documentId) ||
      "a screenshot receipt must identify its matching snapshot",
  ),
);

export const AgentBrowserUiProjectionSchema = Schema.Struct({
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  threadId: ThreadIdSchema,
  runId: BoundedIdentifierSchema,
  updatedAt: IsoDateTimeSchema,
  output: AgentBrowserToolOutputSchema,
  screenshotDataBase64: Schema.optionalKey(AgentBrowserScreenshotBase64Schema),
}).check(
  Schema.makeFilter(
    (projection) =>
      (projection.screenshotDataBase64 === undefined) ===
        (projection.output.screenshot === undefined) ||
      "screenshot bytes and receipt must be present together",
  ),
  Schema.makeFilter(
    (projection) =>
      projection.screenshotDataBase64 === undefined ||
      decodedBase64Length(projection.screenshotDataBase64) ===
        projection.output.screenshot?.byteSize ||
      "projection screenshot bytes must match the receipt",
  ),
);

export const AgentBrowserToolInputStandardSchema = toPapersFlowValidationSchema(
  AgentBrowserToolInputSchema,
);
export const AgentBrowserToolOutputStandardSchema = toPapersFlowValidationSchema(
  AgentBrowserToolOutputSchema,
);
export const AgentBrowserUiProjectionStandardSchema = toPapersFlowValidationSchema(
  AgentBrowserUiProjectionSchema,
);

export const decodeAgentBrowserStartRequest = Schema.decodeUnknownSync(
  AgentBrowserStartRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeAgentBrowserRunScope = Schema.decodeUnknownSync(
  AgentBrowserRunScopeSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeAgentBrowserCommandRequest = Schema.decodeUnknownSync(
  AgentBrowserCommandRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeAgentBrowserCloseRequest = Schema.decodeUnknownSync(
  AgentBrowserCloseRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeAgentBrowserStartResult = Schema.decodeUnknownSync(
  AgentBrowserStartResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeAgentBrowserWorkerTab = Schema.decodeUnknownSync(
  AgentBrowserWorkerTabSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeAgentBrowserWorkerSnapshot = Schema.decodeUnknownSync(
  AgentBrowserWorkerSnapshotSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeAgentBrowserTabId = Schema.decodeUnknownSync(
  AgentBrowserTabIdSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeAgentBrowserDocumentId = Schema.decodeUnknownSync(
  AgentBrowserDocumentIdSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeAgentBrowserSnapshotId = Schema.decodeUnknownSync(
  AgentBrowserSnapshotIdSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeAgentBrowserElementRef = Schema.decodeUnknownSync(
  AgentBrowserElementRefSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeAgentBrowserCommandResult = Schema.decodeUnknownSync(
  AgentBrowserCommandResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeAgentBrowserCloseResult = Schema.decodeUnknownSync(
  AgentBrowserCloseResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeAgentBrowserWorkerEnvelope = Schema.decodeUnknownSync(
  AgentBrowserWorkerEnvelopeSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeAgentBrowserToolOutput = Schema.decodeUnknownSync(
  AgentBrowserToolOutputSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeAgentBrowserToolOutput = Schema.encodeSync(
  AgentBrowserToolOutputSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeAgentBrowserUiProjectionOption = Schema.decodeUnknownOption(
  AgentBrowserUiProjectionSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeAgentBrowserUiProjection = Schema.decodeUnknownSync(
  AgentBrowserUiProjectionSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeAgentBrowserUiProjection = Schema.encodeSync(
  AgentBrowserUiProjectionSchema,
  PapersFlowBoundaryParseOptions,
);

export type AgentBrowserRunScope = typeof AgentBrowserRunScopeSchema.Type;
export type AgentBrowserStartRequest = typeof AgentBrowserStartRequestSchema.Type;
export type AgentBrowserCommandRequest = typeof AgentBrowserCommandRequestSchema.Type;
export type AgentBrowserCloseRequest = typeof AgentBrowserCloseRequestSchema.Type;
export type AgentBrowserCommand = typeof AgentBrowserCommandSchema.Type;
export type AgentBrowserStartResult = typeof AgentBrowserStartResultSchema.Type;
export type AgentBrowserCommandResult = typeof AgentBrowserCommandResultSchema.Type;
export type AgentBrowserCloseResult = typeof AgentBrowserCloseResultSchema.Type;
export type AgentBrowserWorkerEnvelope = typeof AgentBrowserWorkerEnvelopeSchema.Type;
export type AgentBrowserWorkerTab = typeof AgentBrowserWorkerTabSchema.Type;
export type AgentBrowserWorkerSnapshot = typeof AgentBrowserWorkerSnapshotSchema.Type;
export type AgentBrowserWorkerPageText = typeof AgentBrowserWorkerPageTextSchema.Type;
export type AgentBrowserObservedElement = typeof AgentBrowserObservedElementSchema.Type;
export type AgentBrowserSemanticLocator = typeof AgentBrowserSemanticLocatorSchema.Type;
export type AgentBrowserTabId = typeof AgentBrowserTabIdSchema.Type;
export type AgentBrowserSnapshotId = typeof AgentBrowserSnapshotIdSchema.Type;
export type AgentBrowserElementRef = typeof AgentBrowserElementRefSchema.Type;
export type AgentBrowserToolInput = typeof AgentBrowserToolInputSchema.Type;
export type AgentBrowserToolOutput = typeof AgentBrowserToolOutputSchema.Type;
export type AgentBrowserTab = typeof AgentBrowserTabSchema.Type;
export type AgentBrowserSnapshot = typeof AgentBrowserSnapshotSchema.Type;
export type AgentBrowserPageText = typeof AgentBrowserPageTextSchema.Type;
export type AgentBrowserUiProjection = typeof AgentBrowserUiProjectionSchema.Type;
